import * as React from 'react';

import { cn } from '@/lib/utils';

/** Vertical rhythm wrapper for a page section. */
export function Section({
  id,
  className,
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className={cn('relative py-20 sm:py-24 lg:py-28', className)}
    >
      {children}
    </section>
  );
}

/** Small uppercase label with a brand dot. */
export function Eyebrow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink-500',
        className
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-ember-500" />
      {children}
    </span>
  );
}

/** Eyebrow + title + description block used at the top of most sections. */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'center',
  className,
  eyebrowClassName,
  descriptionClassName,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: 'center' | 'left';
  className?: string;
  eyebrowClassName?: string;
  descriptionClassName?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-5',
        align === 'center'
          ? 'mx-auto max-w-2xl items-center text-center'
          : 'items-start text-left',
        className
      )}
    >
      {eyebrow && (
        <Eyebrow {...(eyebrowClassName ? { className: eyebrowClassName } : {})}>
          {eyebrow}
        </Eyebrow>
      )}
      <h2 className="text-balance text-3xl font-medium leading-relaxed tracking-tight text-ink-900 sm:text-4xl lg:text-[2.7rem]">
        {title}
      </h2>
      {description && (
        <p
          className={cn(
            'max-w-2xl text-pretty text-base leading-relaxed text-slate-800 sm:text-lg',
            descriptionClassName
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
}
